import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, ArrowLeft, Receipt, RotateCcw, Trash2, DollarSign, Plus, Minus, Search, Coffee, Cake, Wine, ShoppingBag, CreditCard, Banknote, Smartphone, Split, Package, X, Camera, Barcode, PauseCircle, Globe } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { productAPI, invoiceAPI, returnAPI, discardAPI, categoryAPI } from '../services/api';
import { Html5QrcodeScanner } from 'html5-qrcode';

const Sales = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const username = localStorage.getItem('username') || 'User';
  const userRole = localStorage.getItem('userRole') || 'user';
  const isAdmin = userRole === 'admin';
  const [cartItems, setCartItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [showBillDialog, setShowBillDialog] = useState(false);
  const [finalBillData, setFinalBillData] = useState(null);
  const [productDialogTab, setProductDialogTab] = useState('add');
  const [discountPercent, setDiscountPercent] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Barcode scanning states
  const [barcodeInput, setBarcodeInput] = useState('');
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [scannedOutOfStockProduct, setScannedOutOfStockProduct] = useState(null);
  const barcodeInputRef = useRef(null);
  const scannerRef = useRef(null);
  
  const [newProduct, setNewProduct] = useState({
    name: '',
    brand: '',
    price: '',
    mrp: '',
    sales_price: '',
    unit: 'Pcs',
    stock: '',
    gst: '18',
    category: 'Cafe',
    hsn_code: '',
    barcode: '',
    expiry: ''
  });
  const [selectedProductForUpdate, setSelectedProductForUpdate] = useState(null);
  const [updateData, setUpdateData] = useState({
    name: '',
    mrp: '',
    sales_price: '',
    add_stock: '',
    expiry: ''
  });
  const [updateProductSearch, setUpdateProductSearch] = useState('');
  const [updateCategoryFilter, setUpdateCategoryFilter] = useState('All');
  const [returnInvoiceSearch, setReturnInvoiceSearch] = useState('');
  const [selectedReturnInvoice, setSelectedReturnInvoice] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  const [discardCategory, setDiscardCategory] = useState('All');
  const [discardItems, setDiscardItems] = useState([]);
  const [showDiscardReasonDialog, setShowDiscardReasonDialog] = useState(false);
  const [discardReason, setDiscardReason] = useState('expired');
  const [productQuantities, setProductQuantities] = useState({});
  const [splitPayment, setSplitPayment] = useState({
    card: '',
    cash: '',
    upi: ''
  });
  
  // Category Management state
  const [dbCategories, setDbCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryIcon, setNewCategoryIcon] = useState('Package');
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Hold Cart state (max 2 held carts, stored in localStorage)
  const [heldCarts, setHeldCarts] = useState([]);
  const [selectedHeldCart, setSelectedHeldCart] = useState(null);
  
  // Online Payment state
  const [showOnlineSubMenu, setShowOnlineSubMenu] = useState(false);

  // Load held carts from localStorage on mount
  useEffect(() => {
    const savedHeldCarts = localStorage.getItem('heldCarts');
    if (savedHeldCarts) {
      try {
        setHeldCarts(JSON.parse(savedHeldCarts));
      } catch (e) {
        console.error('Failed to load held carts:', e);
      }
    }
  }, []);

  // Save held carts to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('heldCarts', JSON.stringify(heldCarts));
  }, [heldCarts]);

  // Hold current cart (max 2)
  const holdCurrentCart = () => {
    if (cartItems.length === 0) {
      toast({
        title: "Cannot Hold",
        description: "Cart is empty",
        variant: "destructive"
      });
      return;
    }
    
    if (heldCarts.length >= 2) {
      toast({
        title: "Maximum Held Carts",
        description: "You can only hold up to 2 carts. Please clear or bill a held cart first.",
        variant: "destructive"
      });
      return;
    }
    
    const newHeldCart = {
      id: Date.now(),
      items: [...cartItems],
      total: calculateTotal(),
      timestamp: new Date().toLocaleTimeString()
    };
    
    setHeldCarts([...heldCarts, newHeldCart]);
    setCartItems([]);
    setDiscountPercent('');
    setDiscountAmount('');
    
    toast({
      title: "Cart Held",
      description: `Cart with ${newHeldCart.items.length} items saved`
    });
  };

  // Restore a held cart
  const restoreHeldCart = (cartId) => {
    const cartToRestore = heldCarts.find(c => c.id === cartId);
    if (!cartToRestore) return;
    
    // If current cart has items, ask to hold it first or merge
    if (cartItems.length > 0) {
      toast({
        title: "Current Cart Not Empty",
        description: "Please complete or discard current cart before restoring",
        variant: "destructive"
      });
      return;
    }
    
    setCartItems(cartToRestore.items);
    setHeldCarts(heldCarts.filter(c => c.id !== cartId));
    setSelectedHeldCart(null);
    
    toast({
      title: "Cart Restored",
      description: `Restored cart with ${cartToRestore.items.length} items`
    });
  };

  // Clear a specific held cart
  const clearHeldCart = (cartId) => {
    setHeldCarts(heldCarts.filter(c => c.id !== cartId));
    setSelectedHeldCart(null);
    toast({
      title: "Held Cart Cleared",
      description: "The held cart has been removed"
    });
  };

  // Clear all held carts
  const clearAllHeldCarts = () => {
    setHeldCarts([]);
    setSelectedHeldCart(null);
    toast({
      title: "All Held Carts Cleared"
    });
  };

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await productAPI.getAll();
      // Transform API data to match component's expected format
      const transformedProducts = data.map(p => ({
        id: p.id,
        name: p.name,
        brand: p.brand,
        price: p.price,
        mrp: p.mrp || p.price,
        salesPrice: p.sales_price,
        costPrice: p.cost_price,
        category: p.category,
        stock: p.stock,
        unit: p.unit,
        gst: p.gst,
        hsnCode: p.hsn_code,
        barcode: p.barcode,
        expiry: p.expiry
      }));
      setProducts(transformedProducts);
    } catch (error) {
      console.error('Failed to load products:', error);
      toast({
        title: "Error",
        description: "Failed to load products",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Load categories from database
  const loadCategories = useCallback(async () => {
    try {
      const data = await categoryAPI.getAll();
      setDbCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
      // Fallback to default categories
      setDbCategories([
        { id: '1', name: 'All', icon: 'ShoppingBag' },
        { id: '2', name: 'Cafe', icon: 'Coffee' },
        { id: '3', name: 'Bakery', icon: 'Cake' },
        { id: '4', name: 'Beverage', icon: 'Wine' },
        { id: '5', name: 'Daily Object', icon: 'Package' }
      ]);
    }
  }, []);

  const addToCart = useCallback((product, quantity = 1) => {
    if (quantity > product.stock) {
      toast({
        title: "Insufficient Stock",
        description: `Only ${product.stock} units available`,
        variant: "destructive"
      });
      return;
    }

    setCartItems(prevCartItems => {
      const existingItem = prevCartItems.find(item => item.id === product.id);
      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        if (newQuantity > product.stock) {
          toast({
            title: "Insufficient Stock",
            description: `Only ${product.stock} units available`,
            variant: "destructive"
          });
          return prevCartItems;
        }
        return prevCartItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: newQuantity }
            : item
        );
      } else {
        return [...prevCartItems, { ...product, quantity }];
      }
    });
    
    setProductQuantities(prev => ({ ...prev, [product.id]: 1 }));
    
    toast({
      title: "Item Added",
      description: `${quantity} x ${product.name} added to cart`
    });
  }, [toast]);

  // Barcode scanning handlers
  const handleBarcodeSubmit = useCallback(async (barcode) => {
    if (!barcode || barcode.trim() === '') return;
    
    try {
      const product = await productAPI.getByBarcode(barcode.trim());
      
      // Transform API data to match component format
      const transformedProduct = {
        id: product.id,
        name: product.name,
        brand: product.brand,
        price: product.price,
        mrp: product.mrp || product.price,
        salesPrice: product.sales_price,
        costPrice: product.cost_price,
        category: product.category,
        stock: product.stock,
        unit: product.unit,
        gst: product.gst,
        hsnCode: product.hsn_code,
        barcode: product.barcode,
        expiry: product.expiry
      };
      
      // Check if product is out of stock
      if (product.stock <= 0) {
        toast({
          title: "Product Found - Out of Stock",
          description: `${product.name} is out of stock (0 available). Cannot add to cart.`,
          variant: "destructive"
        });
        // Set the scanned out of stock product to highlight it in the product list
        setScannedOutOfStockProduct(transformedProduct);
      } else {
        // Add to cart only if stock > 0
        addToCart(transformedProduct, 1);
        setScannedOutOfStockProduct(null);
        toast({
          title: "Product Scanned",
          description: `${product.name} added to cart`
        });
      }
      
    } catch (error) {
      toast({
        title: "Product Not Found",
        description: `No product found with barcode: ${barcode}`,
        variant: "destructive"
      });
    }
    
    setBarcodeInput('');
    // Refocus the barcode input for continuous scanning
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [toast, addToCart]);

  // Handle barcode input from USB scanner (listens for Enter key)
  const handleBarcodeKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeSubmit(barcodeInput);
    }
  };

  // Camera scanner functions
  const startCameraScanner = () => {
    setShowCameraScanner(true);
  };

  const stopCameraScanner = () => {
    if (scannerRef.current) {
      scannerRef.current.clear();
      scannerRef.current = null;
    }
    setShowCameraScanner(false);
  };

  useEffect(() => {
    if (showCameraScanner) {
      const scanner = new Html5QrcodeScanner(
        "barcode-scanner",
        { fps: 10, qrbox: { width: 250, height: 100 } },
        false
      );
      
      scanner.render(
        (decodedText) => {
          handleBarcodeSubmit(decodedText);
          stopCameraScanner();
        },
        (error) => {
          // Ignore scanning errors
        }
      );
      
      scannerRef.current = scanner;
    }
    
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear();
      }
    };
  }, [showCameraScanner, handleBarcodeSubmit]);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    
    loadProducts();
    loadCategories();
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('action') === 'addProduct') {
      setShowProductDialog(true);
    }
  }, [navigate, loadProducts, loadCategories]);

  const handleLogout = () => {
    navigate('/dashboard?action=logout');
  };

  const handleQuickAction = (action) => {
    setSelectedAction(action);
    
    if (action === 'Return') {
      toast({
        title: `${action} Mode Activated`,
        description: `Search invoice number to process return`
      });
    } else if (action === 'Discard') {
      toast({
        title: `${action} Mode Activated`,
        description: `Select category and products to discard`
      });
    } else {
      toast({
        title: `${action} Mode Activated`,
        description: `You are now in ${action.toLowerCase()} mode`
      });
    }
  };

  // Icon mapping for categories
  const iconMap = {
    ShoppingBag, Coffee, Cake, Wine, Package
  };

  // Derived categories with icon components
  const categories = dbCategories.map(cat => ({
    ...cat,
    icon: iconMap[cat.icon] || Package
  }));

  // Add category
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast({
        title: "Error",
        description: "Category name is required",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await categoryAPI.create({ name: newCategoryName.trim(), icon: newCategoryIcon });
      await loadCategories();
      setNewCategoryName('');
      setNewCategoryIcon('Package');
      toast({
        title: "Category Added",
        description: `${newCategoryName} has been added`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Update category
  const handleUpdateCategory = async () => {
    if (!editingCategory || !editingCategory.name.trim()) return;
    
    try {
      await categoryAPI.update(editingCategory.id, { 
        name: editingCategory.name.trim(), 
        icon: editingCategory.icon 
      });
      await loadCategories();
      setEditingCategory(null);
      toast({
        title: "Category Updated",
        description: "Category has been updated"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  // Delete category
  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (categoryName === 'All') {
      toast({
        title: "Cannot Delete",
        description: "The 'All' category cannot be deleted",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await categoryAPI.delete(categoryId);
      await loadCategories();
      toast({
        title: "Category Deleted",
        description: `${categoryName} has been deleted`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Ensure scanned out-of-stock product is included in the displayed list
  const displayProducts = scannedOutOfStockProduct 
    ? [scannedOutOfStockProduct, ...filteredProducts.filter(p => p.id !== scannedOutOfStockProduct.id)]
    : filteredProducts;

  const getProductQuantity = (productId) => {
    return productQuantities[productId] || 1;
  };

  const setProductQuantity = (productId, quantity, maxStock) => {
    const newQty = Math.max(1, Math.min(quantity, maxStock));
    setProductQuantities({ ...productQuantities, [productId]: newQty });
  };

  const updateQuantity = (id, change) => {
    setCartItems(cartItems.map(item => {
      if (item.id === id) {
        const newQuantity = item.quantity + change;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (id) => {
    setCartItems(cartItems.filter(item => item.id !== id));
  };

  const calculateTotal = () => {
    return Math.round(cartItems.reduce((sum, item) => {
      const price = selectedAction === 'Cost Price' ? item.costPrice : (item.salesPrice || item.price);
      return sum + (price * item.quantity);
    }, 0) * 100) / 100;
  };

  const calculateDiscount = () => {
    const subtotal = calculateTotal();
    let discount = 0;
    
    if (discountPercent && parseFloat(discountPercent) > 0) {
      discount = (subtotal * parseFloat(discountPercent)) / 100;
    } else if (discountAmount && parseFloat(discountAmount) > 0) {
      discount = parseFloat(discountAmount);
    }
    
    return Math.round(Math.min(discount, subtotal) * 100) / 100;
  };

  const calculateFinalTotal = () => {
    return Math.round((calculateTotal() - calculateDiscount()) * 100) / 100;
  };

  const handleDiscountPercentChange = (value) => {
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setDiscountPercent(value);
      setDiscountAmount('');
    }
  };

  const handleDiscountAmountChange = (value) => {
    if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
      setDiscountAmount(value);
      setDiscountPercent('');
    }
  };

  const handleDiscard = () => {
    setCartItems([]);
    setSelectedAction(null);
    setDiscountPercent('');
    setDiscountAmount('');
    toast({
      title: "Cart Discarded",
      description: "All items removed from cart"
    });
  };

  const handleBilling = () => {
    if (cartItems.length === 0) {
      toast({
        title: "Empty Cart",
        description: "Please add items to cart before billing",
        variant: "destructive"
      });
      return;
    }
    setShowPaymentDialog(true);
  };

  const handlePaymentMethod = (method) => {
    if (method === 'Split') {
      setShowPaymentDialog(false);
      setShowSplitDialog(true);
    } else if (method === 'Online') {
      setShowOnlineSubMenu(true);
    } else if (method === 'Online - Zomato' || method === 'Online - Swiggy') {
      setShowOnlineSubMenu(false);
      processPayment(method);
    } else {
      processPayment(method);
    }
  };

  const processPayment = async (method, splitDetails = null) => {
    const total = calculateFinalTotal();
    
    // Helper to round to 2 decimal places
    const r2 = (n) => Math.round(n * 100) / 100;
    
    // Calculate GST breakdown
    const itemsWithGST = cartItems.map(item => {
      const sellingPrice = selectedAction === 'Cost Price' ? item.costPrice : (item.salesPrice || item.price);
      const itemTotal = r2(sellingPrice * item.quantity);
      const gstAmount = r2((itemTotal * item.gst) / (100 + item.gst));
      const cgst = r2(gstAmount / 2);
      const sgst = r2(gstAmount / 2);
      return {
        product_id: item.id,
        name: item.name,
        brand: item.brand,
        category: item.category,
        barcode: item.barcode,
        quantity: item.quantity,
        price: r2(item.price),
        cost_price: r2(item.costPrice),
        sales_price: r2(sellingPrice),
        item_total: itemTotal,
        gst: item.gst,
        cgst,
        sgst
      };
    });
    
    const totalCGST = r2(itemsWithGST.reduce((sum, item) => sum + item.cgst, 0));
    const totalSGST = r2(itemsWithGST.reduce((sum, item) => sum + item.sgst, 0));
    const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    
    const totalSavings = r2(cartItems.reduce((sum, item) => {
      const mrp = item.price;
      const selling = selectedAction === 'Cost Price' ? item.costPrice : (item.salesPrice || item.price);
      return sum + ((mrp - selling) * item.quantity);
    }, 0));
    
    const subtotalBeforeDiscount = r2(total + calculateDiscount());
    const discountApplied = calculateDiscount();
    
    const invoiceData = {
      items: itemsWithGST,
      total_quantity: totalQuantity,
      subtotal: r2(subtotalBeforeDiscount - totalCGST - totalSGST),
      cgst: totalCGST,
      sgst: totalSGST,
      discount: discountApplied,
      discount_percent: discountPercent ? parseFloat(discountPercent) : 0,
      total: r2(total),
      savings: r2(totalSavings + discountApplied),
      payment_method: method,
      payment_details: splitDetails
    };

    try {
      const createdInvoice = await invoiceAPI.create(invoiceData);
      
      setFinalBillData(createdInvoice);
      setShowBillDialog(true);
      
      // Refresh products to get updated stock
      await loadProducts();
      
      toast({
        title: "Bill Generated Successfully!",
        description: `Invoice No: ${createdInvoice.invoice_no}`
      });
      
      setCartItems([]);
      setSelectedAction(null);
      setDiscountPercent('');
      setDiscountAmount('');
      setShowPaymentDialog(false);
      setShowSplitDialog(false);
      setSplitPayment({ card: '', cash: '', upi: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create invoice",
        variant: "destructive"
      });
    }
  };

  const calculateSplitTotal = () => {
    const card = parseFloat(splitPayment.card) || 0;
    const cash = parseFloat(splitPayment.cash) || 0;
    const upi = parseFloat(splitPayment.upi) || 0;
    return Math.round((card + cash + upi) * 100) / 100;
  };

  const handleFinalizeSplit = () => {
    const splitTotal = calculateSplitTotal();
    const cartTotal = calculateFinalTotal();
    
    if (Math.abs(splitTotal - cartTotal) > 0.01) {
      toast({
        title: "Payment Mismatch",
        description: `Split total (₹${splitTotal.toFixed(2)}) doesn't match bill total (₹${cartTotal.toFixed(2)})`,
        variant: "destructive"
      });
      return;
    }
    
    if (splitTotal === 0) {
      toast({
        title: "Invalid Payment",
        description: "Please enter payment amounts",
        variant: "destructive"
      });
      return;
    }
    
    processPayment('Split', splitPayment);
  };

  const handleAddNewProduct = async () => {
    if (!newProduct.name || !newProduct.brand || !newProduct.price || !newProduct.sales_price || !newProduct.stock || !newProduct.hsn_code) {
      toast({
        title: "Missing Fields",
        description: "Please fill all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const productData = {
        name: newProduct.name,
        brand: newProduct.brand,
        price: parseFloat(newProduct.price),
        mrp: parseFloat(newProduct.mrp || newProduct.price),
        sales_price: parseFloat(newProduct.sales_price),
        cost_price: parseFloat(newProduct.price) * 0.6,
        category: newProduct.category,
        stock: parseInt(newProduct.stock),
        unit: newProduct.unit,
        gst: parseInt(newProduct.gst),
        hsn_code: newProduct.hsn_code,
        barcode: newProduct.barcode || null,
        expiry: newProduct.category === 'Bakery' ? newProduct.expiry : null
      };

      await productAPI.create(productData);
      
      toast({
        title: "Product Added",
        description: `${newProduct.name} has been added to ${newProduct.category} category`
      });

      setNewProduct({
        name: '',
        brand: '',
        price: '',
        mrp: '',
        sales_price: '',
        unit: 'Pcs',
        stock: '',
        gst: '18',
        category: 'Cafe',
        hsn_code: '',
        barcode: '',
        expiry: ''
      });
      
      await loadProducts();
      setShowProductDialog(false);
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to add product",
        variant: "destructive"
      });
    }
  };

  const handleUpdateProduct = async () => {
    if (!selectedProductForUpdate) return;

    try {
      const updatePayload = {};
      if (updateData.name) updatePayload.name = updateData.name;
      if (updateData.mrp) updatePayload.mrp = parseFloat(updateData.mrp);
      if (updateData.sales_price) updatePayload.sales_price = parseFloat(updateData.sales_price);
      if (updateData.add_stock) updatePayload.add_stock = parseInt(updateData.add_stock);
      if (updateData.expiry) updatePayload.expiry = updateData.expiry;
      if (updateData.category) updatePayload.category = updateData.category;

      await productAPI.update(selectedProductForUpdate.id, updatePayload);

      toast({
        title: "Product Updated",
        description: `${selectedProductForUpdate.name} has been updated successfully`
      });

      await loadProducts();
      setSelectedProductForUpdate(null);
      setUpdateData({ name: '', mrp: '', sales_price: '', add_stock: '', expiry: '', category: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update product",
        variant: "destructive"
      });
    }
  };

  const handleDeleteProduct = async (productId) => {
    try {
      await productAPI.delete(productId);
      await loadProducts();
      toast({
        title: "Product Deleted",
        description: "Product has been removed from the system"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete product",
        variant: "destructive"
      });
    }
  };

  const handleReturnInvoiceSearch = async () => {
    try {
      const invoice = await invoiceAPI.getByInvoiceNo(returnInvoiceSearch);
      setSelectedReturnInvoice(invoice);
      setReturnItems([]);
      toast({
        title: "Invoice Found",
        description: `Invoice ${invoice.invoice_no} loaded for return`
      });
    } catch (error) {
      toast({
        title: "Invoice Not Found",
        description: "Please check the invoice number",
        variant: "destructive"
      });
    }
  };

  const handleAddToReturn = (item) => {
    const existingItem = returnItems.find(ri => ri.product_id === item.product_id);
    if (existingItem) {
      toast({
        title: "Already Added",
        description: "Item already in return list. Adjust quantity below.",
        variant: "destructive"
      });
      return;
    }
    setReturnItems([...returnItems, { ...item, returnQuantity: 1 }]);
  };

  const updateReturnQuantity = (productId, quantity) => {
    setReturnItems(returnItems.map(ri =>
      ri.product_id === productId ? { ...ri, returnQuantity: Math.max(1, Math.min(quantity, ri.quantity)) } : ri
    ));
  };

  const removeFromReturn = (productId) => {
    setReturnItems(returnItems.filter(ri => ri.product_id !== productId));
  };

  const handleProcessReturn = async () => {
    if (returnItems.length === 0) {
      toast({
        title: "No Items",
        description: "Please select items to return",
        variant: "destructive"
      });
      return;
    }

    try {
      const totalReturnAmount = returnItems.reduce((sum, item) => 
        sum + (item.sales_price * item.returnQuantity), 0);
      
      await returnAPI.create({
        invoice_no: selectedReturnInvoice.invoice_no,
        items: returnItems.map(item => ({
          product_id: item.product_id,
          name: item.name,
          return_quantity: item.returnQuantity,
          price: item.sales_price
        })),
        total_return_amount: totalReturnAmount
      });

      await loadProducts();

      toast({
        title: "Return Processed",
        description: `${returnItems.length} items returned successfully`
      });

      setReturnItems([]);
      setSelectedReturnInvoice(null);
      setReturnInvoiceSearch('');
      setSelectedAction(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to process return",
        variant: "destructive"
      });
    }
  };

  const handleAddToDiscard = (product, quantity) => {
    const existingItem = discardItems.find(di => di.id === product.id);
    if (existingItem) {
      setDiscardItems(discardItems.map(di =>
        di.id === product.id ? { ...di, quantity } : di
      ));
    } else {
      setDiscardItems([...discardItems, { ...product, quantity }]);
    }
  };

  const handleProcessDiscard = () => {
    if (discardItems.length === 0) {
      toast({
        title: "No Items",
        description: "Please select items to discard",
        variant: "destructive"
      });
      return;
    }
    setShowDiscardReasonDialog(true);
  };

  const handleFinalDiscard = async () => {
    try {
      await discardAPI.create({
        items: discardItems.map(item => ({
          product_id: item.id,
          name: item.name,
          quantity: item.quantity
        })),
        reason: discardReason
      });

      await loadProducts();

      toast({
        title: "Items Discarded",
        description: `${discardItems.length} items discarded - Reason: ${discardReason}`
      });

      setDiscardItems([]);
      setShowDiscardReasonDialog(false);
      setDiscardReason('expired');
      setDiscardCategory('All');
      setSelectedAction(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to discard items",
        variant: "destructive"
      });
    }
  };

  const handleReturn = () => {
    toast({
      title: "Return Mode",
      description: "Scan or select items to return"
    });
  };

  const printBill = () => {
    if (!finalBillData) return;
    
    const printWindow = window.open('', '', 'width=320,height=600');
    
    if (!printWindow) {
      toast({
        title: "Print Blocked",
        description: "Please allow popups to print",
        variant: "destructive"
      });
      return;
    }
    
    // Generate product rows HTML
    const productRowsHTML = finalBillData.items.map(item => {
      const productName = item.name;
      const qty = item.quantity;
      const mrp = (item.price || 0).toFixed(2);
      const sellingPrice = (item.sales_price || item.price || 0).toFixed(2);
      const amt = (item.item_total || 0).toFixed(2);
      
      return `
        <tr class="product-row">
          <td class="product-name">${productName}</td>
          <td class="center">${qty}</td>
          <td class="center">${mrp}</td>
          <td class="center">${sellingPrice}</td>
          <td class="right">${amt}</td>
        </tr>
      `;
    }).join('');
    
    const totalQty = finalBillData.total_quantity || 0;
    const subtotal = (finalBillData.subtotal || 0).toFixed(2);
    const cgst = (finalBillData.cgst || 0).toFixed(2);
    const sgst = (finalBillData.sgst || 0).toFixed(2);
    const total = (finalBillData.total || 0).toFixed(2);
    const savings = (finalBillData.savings || 0).toFixed(2);
    const totalItems = finalBillData.items?.length || 0;
    const orderAmount = subtotal;
    const totalPayable = total;
    
    const billHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${finalBillData.invoice_no}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: 80mm auto; margin: 0; }
          html, body { 
            width: 80mm; 
            max-width: 80mm;
            margin: 0; 
            padding: 0; 
            background: #fff; 
            color: #000;
            font-family: 'Arial', 'Helvetica', sans-serif;
            font-size: 11px;
            line-height: 1.3;
          }
          .receipt {
            width: 100%;
            padding: 2mm;
          }
          .center { text-align: center; }
          .left { text-align: left; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          
          /* Header */
          .header-text { font-size: 10px; margin-bottom: 2px; }
          .title { font-size: 14px; font-weight: bold; margin: 4px 0; }
          
          /* Logo Section */
          .logo-section {
            display: flex;
            justify-content: center;
            align-items: center;
            margin: 6px 0;
            gap: 3px;
          }
          .logo-block {
            background: #000;
            width: 18px;
            height: 28px;
            display: flex;
            flex-direction: column;
            justify-content: space-evenly;
            padding: 2px 0;
          }
          .logo-line {
            background: #fff;
            height: 2px;
            width: 100%;
          }
          .shop-bubble {
            background: #000;
            color: #fff;
            padding: 4px 8px;
            border-radius: 4px;
            font-size: 12px;
            font-weight: bold;
            position: relative;
          }
          .shop-bubble::after {
            content: '';
            position: absolute;
            bottom: -4px;
            left: 50%;
            transform: translateX(-50%);
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            border-top: 4px solid #000;
          }
          
          /* Info Rows */
          .info-row {
            display: flex;
            justify-content: space-between;
            margin: 1px 0;
            font-size: 10px;
          }
          .info-label { font-weight: normal; }
          .info-value { text-align: right; }
          
          /* Dotted Separator */
          .dotted-line {
            border: none;
            border-top: 1px dashed #000;
            margin: 4px 0;
          }
          .solid-line {
            border: none;
            border-top: 2px solid #000;
            margin: 4px 0;
          }
          
          /* Product Table */
          .product-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 9px;
            margin: 4px 0;
          }
          .product-table th {
            font-weight: bold;
            padding: 2px 1px;
            text-align: center;
            border-bottom: 1px dashed #000;
          }
          .product-table th:first-child { text-align: left; }
          .product-table th:last-child { text-align: right; }
          .product-table td {
            padding: 2px 1px;
            vertical-align: top;
          }
          .product-name {
            text-align: left;
            word-break: break-word;
            max-width: 90px;
            font-size: 9px;
          }
          .product-row td {
            border-bottom: none;
          }
          
          /* Summary Section */
          .summary-row {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            margin: 2px 0;
          }
          .qty-total-row {
            display: flex;
            justify-content: space-between;
            font-size: 10px;
            font-weight: bold;
            margin: 4px 0;
            padding: 2px 0;
            border-top: 1px dashed #000;
          }
          
          /* GST Table */
          .gst-table {
            width: 100%;
            font-size: 9px;
            margin: 4px 0;
          }
          .gst-table td {
            text-align: center;
            padding: 1px 2px;
          }
          .gst-header td {
            font-weight: bold;
            border-bottom: 1px dashed #000;
          }
          
          /* Totals Section */
          .totals-section {
            margin-top: 4px;
          }
          .total-payable {
            display: flex;
            justify-content: space-between;
            font-size: 12px;
            font-weight: bold;
            padding: 4px 0;
            border-top: 2px solid #000;
            border-bottom: 2px solid #000;
            margin: 4px 0;
          }
          
          /* Savings Section */
          .savings-section {
            display: flex;
            justify-content: space-between;
            font-size: 14px;
            font-weight: bold;
            margin: 8px 0;
            padding: 4px 0;
          }
          
          /* Footer */
          .footer {
            text-align: center;
            font-size: 9px;
            font-weight: bold;
            margin-top: 6px;
            padding-top: 4px;
            border-top: 2px solid #000;
          }
          .footer-line { margin: 2px 0; }
          
          @media print {
            html, body { width: 80mm; }
            .receipt { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <!-- Header -->
          <div class="center header-text">See you again</div>
          <div class="center title">RETAIL INVOICE</div>
          
          <!-- Shop Name -->
          <div class="center" style="font-size: 16px; font-weight: bold; margin: 8px 0; padding: 4px 0; border-top: 1px solid #000; border-bottom: 1px solid #000;">New Shop</div>
          
          <!-- Invoice Info -->
          <div class="info-row">
            <span class="info-label">Invoice No:</span>
            <span class="info-value">${finalBillData.invoice_no}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Store</span>
            <span class="info-value">Thane Mira Bhayandar</span>
          </div>
          <div class="info-row">
            <span class="info-label">Address</span>
            <span class="info-value" style="text-align: right; max-width: 55%; font-size: 9px;">Ground Floor Shop No 5 & 6 Ostwal Ashadeep Near Silver Park Signal East Mira Bhayandar Road Thane Maharashtra 401107</span>
          </div>
          <div class="info-row">
            <span class="info-label">Pincode</span>
            <span class="info-value">401107</span>
          </div>
          <div class="info-row">
            <span class="info-label">Phone</span>
            <span class="info-value">7718936910</span>
          </div>
          <div class="info-row">
            <span class="info-label">GSTIN</span>
            <span class="info-value">27BSTPM1629P1ZU</span>
          </div>
          <div class="info-row">
            <span class="info-label">FSSAI</span>
            <span class="info-value">11525020000225</span>
          </div>
          <div class="info-row">
            <span class="info-label">Date</span>
            <span class="info-value">${finalBillData.date}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Time</span>
            <span class="info-value">${finalBillData.time}</span>
          </div>
          
          <hr class="dotted-line" />
          
          <!-- Payment Info -->
          <div class="info-row">
            <span class="info-label bold">Payment Type</span>
            <span class="info-value bold">Order From</span>
          </div>
          <div class="info-row">
            <span class="info-label">${finalBillData.payment_method === 'Split' && finalBillData.payment_details 
              ? 'Split - ' + [
                  finalBillData.payment_details.cash ? 'Cash ₹' + parseFloat(finalBillData.payment_details.cash).toFixed(2) : '',
                  finalBillData.payment_details.upi ? 'UPI ₹' + parseFloat(finalBillData.payment_details.upi).toFixed(2) : '',
                  finalBillData.payment_details.card ? 'Card ₹' + parseFloat(finalBillData.payment_details.card).toFixed(2) : ''
                ].filter(Boolean).join(' + ')
              : (finalBillData.payment_method || 'Cash')}</span>
            <span class="info-value">pos</span>
          </div>
          
          <hr class="dotted-line" />
          
          <!-- Product Table -->
          <table class="product-table">
            <thead>
              <tr>
                <th style="text-align: left; width: 35%;">Product</th>
                <th style="width: 10%;">Qty</th>
                <th style="width: 18%;">MRP</th>
                <th style="width: 18%;">Selling Price</th>
                <th style="text-align: right; width: 19%;">Amt</th>
              </tr>
            </thead>
            <tbody>
              ${productRowsHTML}
            </tbody>
          </table>
          
          <!-- Quantity & Total -->
          <div class="qty-total-row">
            <span>Quantity</span>
            <span>${totalQty}</span>
            <span>Total</span>
            <span>${(parseFloat(subtotal) + parseFloat(cgst) + parseFloat(sgst)).toFixed(2)}</span>
          </div>
          
          <hr class="dotted-line" />
          
          <!-- GST Breakdown -->
          <table class="gst-table">
            <tr class="gst-header">
              <td>CGST</td>
              <td>SGST</td>
              <td>CESS</td>
              <td>ACESS</td>
            </tr>
            <tr>
              <td>${cgst}</td>
              <td>${sgst}</td>
              <td>0.00</td>
              <td>0.00</td>
            </tr>
          </table>
          
          <hr class="dotted-line" />
          
          <!-- Final Totals -->
          <div class="totals-section">
            <div class="summary-row">
              <span>Total Items</span>
              <span>${totalItems}</span>
            </div>
            <div class="summary-row">
              <span>Order Amount</span>
              <span>${(parseFloat(subtotal) + parseFloat(cgst) + parseFloat(sgst)).toFixed(2)}</span>
            </div>
            
            <div class="total-payable">
              <span>TOTAL PAYABLE</span>
              <span>${totalPayable}</span>
            </div>
          </div>
          
          <!-- Savings -->
          <div class="savings-section">
            <span>TOTAL SAVINGS</span>
            <span>${savings}</span>
          </div>
          
          <!-- Footer -->
          <div class="footer">
            <div class="footer-line">THANK YOU FOR SHOPPING WITH US</div>
            <div class="footer-line">NO RETURN - NO EXCHANGE AFTER BILLING</div>
          </div>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(billHTML);
    printWindow.document.close();
    
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
          setShowBillDialog(false);
        }, 1000);
      }, 300);
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#e8ecf0' }}>
        <div className="text-xl text-gray-600">Loading products...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#e8ecf0' }}>
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <img 
              src="https://customer-assets.emergentagent.com/job_6a116685-1875-4e0c-97f5-a9213e7bf53a/artifacts/isz5lodv_IMG_6110.PNG" 
              alt="New Shop Logo" 
              className="h-10 object-contain"
              data-testid="sales-logo"
            />
            <span className="text-xl font-semibold text-gray-800">New Sale</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-gray-600">Welcome, <span className="font-semibold">{username}</span></span>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Quick Actions */}
        <Card className="bg-white mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Button
                onClick={() => setShowProductDialog(true)}
                className="h-20 flex flex-col items-center justify-center space-y-2 transition-all bg-gray-800 hover:bg-gray-900 text-white"
                data-testid="add-product-btn"
              >
                <Package className="w-6 h-6" />
                <span className="font-semibold">Add Product</span>
              </Button>
              
              <Button
                onClick={() => handleQuickAction('Billing')}
                className={`h-20 flex flex-col items-center justify-center space-y-2 transition-all ${
                  selectedAction === 'Billing'
                    ? 'bg-yellow-400 hover:bg-yellow-500 text-black'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                <Receipt className="w-6 h-6" />
                <span className="font-semibold">Billing</span>
              </Button>

              <Button
                onClick={() => handleQuickAction('Return')}
                className={`h-20 flex flex-col items-center justify-center space-y-2 transition-all ${
                  selectedAction === 'Return'
                    ? 'bg-yellow-400 hover:bg-yellow-500 text-black'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                <RotateCcw className="w-6 h-6" />
                <span className="font-semibold">Return</span>
              </Button>

              <Button
                onClick={() => handleQuickAction('Discard')}
                className={`h-20 flex flex-col items-center justify-center space-y-2 transition-all ${
                  selectedAction === 'Discard'
                    ? 'bg-yellow-400 hover:bg-yellow-500 text-black'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                <Trash2 className="w-6 h-6" />
                <span className="font-semibold">Discard</span>
              </Button>

              <Button
                onClick={() => handleQuickAction('Cost Price')}
                className={`h-20 flex flex-col items-center justify-center space-y-2 transition-all ${
                  selectedAction === 'Cost Price'
                    ? 'bg-yellow-400 hover:bg-yellow-500 text-black'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                }`}
              >
                <DollarSign className="w-6 h-6" />
                <span className="font-semibold">Cost Price</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Return Invoice Search */}
        {selectedAction === 'Return' && (
          <Card className="bg-white mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Search Invoice for Return</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex space-x-2 mb-4">
                <Input
                  placeholder="Enter Invoice Number (e.g., RRC-0326-123456)"
                  value={returnInvoiceSearch}
                  onChange={(e) => setReturnInvoiceSearch(e.target.value)}
                  className="flex-1"
                />
                <Button onClick={handleReturnInvoiceSearch} className="bg-yellow-400 hover:bg-yellow-500 text-black">
                  Search
                </Button>
              </div>

              {selectedReturnInvoice && (
                <div className="space-y-2">
                  <div className="bg-gray-100 p-4 rounded">
                    <p className="font-semibold">Invoice: {selectedReturnInvoice.invoice_no}</p>
                    <p className="text-sm">Date: {selectedReturnInvoice.date} | Time: {selectedReturnInvoice.time}</p>
                    <p className="text-sm">Total: ₹{selectedReturnInvoice.total.toFixed(2)}</p>
                  </div>
                  
                  <div>
                    <p className="font-semibold mb-2">Select Items to Return:</p>
                    <div className="grid grid-cols-1 gap-2">
                      {selectedReturnInvoice.items.map((item, index) => (
                        <div key={index} className="border rounded p-3 flex justify-between items-center">
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-gray-600">Original Qty: {item.quantity} | Price: ₹{item.item_total.toFixed(2)}</p>
                          </div>
                          <Button
                            onClick={() => handleAddToReturn(item)}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            disabled={returnItems.some(ri => ri.product_id === item.product_id)}
                          >
                            {returnItems.some(ri => ri.product_id === item.product_id) ? 'Added' : 'Add to Return'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {returnItems.length > 0 && (
                    <div className="mt-4 p-4 bg-yellow-50 rounded border border-yellow-400">
                      <p className="font-semibold mb-3">Items to Return ({returnItems.length}):</p>
                      <div className="space-y-2 mb-3">
                        {returnItems.map((item) => (
                          <div key={item.product_id} className="flex items-center justify-between bg-white p-2 rounded">
                            <div className="flex-1">
                              <p className="font-medium text-sm">{item.name}</p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Button
                                onClick={() => updateReturnQuantity(item.product_id, item.returnQuantity - 1)}
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0"
                                disabled={item.returnQuantity <= 1}
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="w-8 text-center text-sm font-semibold">{item.returnQuantity}</span>
                              <Button
                                onClick={() => updateReturnQuantity(item.product_id, item.returnQuantity + 1)}
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0"
                                disabled={item.returnQuantity >= item.quantity}
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                              <Button
                                onClick={() => removeFromReturn(item.product_id)}
                                variant="outline"
                                size="sm"
                                className="h-7 w-7 p-0 text-red-600"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button onClick={handleProcessReturn} className="w-full bg-green-600 hover:bg-green-700 text-white">
                        Process Return
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Cost Price View */}
        {selectedAction === 'Cost Price' && (
          <Card className="bg-white mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Cost Price Reference</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="flex-1">
                  <Label className="mb-2 block">Filter by Category</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All">All Categories</SelectItem>
                      <SelectItem value="Cafe">Cafe</SelectItem>
                      <SelectItem value="Bakery">Bakery</SelectItem>
                      <SelectItem value="Beverage">Beverage</SelectItem>
                      <SelectItem value="Daily Object">Daily Object</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="mb-2 block">Search Product</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="border rounded-lg p-3 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.brand} • {product.category}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded font-medium ${
                        product.stock <= 0 ? 'bg-red-100 text-red-800' :
                        product.stock < 10 ? 'bg-orange-100 text-orange-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {product.stock} {product.unit}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">MRP:</span>
                        <span className="line-through text-gray-400">₹{product.price}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Sales:</span>
                        <span className="text-green-600 font-medium">₹{product.salesPrice}</span>
                      </div>
                      <div className="flex justify-between bg-blue-50 p-1 rounded">
                        <span className="text-blue-700 font-medium">Cost:</span>
                        <span className="text-blue-700 font-bold">₹{product.costPrice}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Discard Section */}
        {selectedAction === 'Discard' && (
          <Card className="bg-white mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Select Products to Discard</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Label>Filter by Category</Label>
                <Select value={discardCategory} onValueChange={setDiscardCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Categories</SelectItem>
                    <SelectItem value="Cafe">Cafe</SelectItem>
                    <SelectItem value="Bakery">Bakery</SelectItem>
                    <SelectItem value="Beverage">Beverage</SelectItem>
                    <SelectItem value="Daily Object">Daily Object</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 gap-2">
                {products.filter(p => discardCategory === 'All' || p.category === discardCategory).map((product) => (
                  <div key={product.id} className="border rounded p-3 flex justify-between items-center">
                    <div className="flex-1">
                      <p className="font-medium">{product.name}</p>
                      <p className="text-sm text-gray-600">Stock: {product.stock} {product.unit} | Category: {product.category}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        placeholder="Qty"
                        min="1"
                        max={product.stock}
                        className="w-20"
                        onChange={(e) => {
                          const qty = parseInt(e.target.value) || 0;
                          if (qty > 0 && qty <= product.stock) {
                            handleAddToDiscard(product, qty);
                          }
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {discardItems.length > 0 && (
                <div className="mt-4 p-4 bg-red-50 rounded">
                  <p className="font-semibold mb-2">Items to Discard: {discardItems.length}</p>
                  <Button onClick={handleProcessDiscard} className="w-full bg-red-600 hover:bg-red-700 text-white">
                    Final Discard
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Sales Interface */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Products Section */}
          {(selectedAction === 'Billing' || !selectedAction) && (
            <div className="lg:col-span-2">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="text-lg">Products</CardTitle>
                  
                  {/* Category Tabs */}
                  <div className="mt-4">
                    <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-full">
                      <TabsList className="grid w-full grid-cols-5 gap-2">
                        {categories.map((category) => {
                          const Icon = category.icon;
                          return (
                            <TabsTrigger 
                              key={category.name} 
                              value={category.name}
                              className="flex items-center space-x-1 data-[state=active]:bg-yellow-400 data-[state=active]:text-black"
                            >
                              <Icon className="w-4 h-4" />
                              <span className="text-xs">{category.name}</span>
                            </TabsTrigger>
                          );
                        })}
                      </TabsList>
                    </Tabs>
                  </div>
                  
                  {/* Search Bar */}
                  <div className="mt-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search products..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  {/* Barcode Scanner Section */}
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <Barcode className="w-5 h-5 text-yellow-700" />
                      <span className="font-semibold text-yellow-800">Barcode Scanner</span>
                    </div>
                    <div className="flex space-x-2">
                      <div className="relative flex-1">
                        <Barcode className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                        <Input
                          ref={barcodeInputRef}
                          type="text"
                          placeholder="Scan barcode or type manually..."
                          value={barcodeInput}
                          onChange={(e) => setBarcodeInput(e.target.value)}
                          onKeyPress={handleBarcodeKeyPress}
                          className="pl-10 font-mono"
                          data-testid="barcode-input"
                        />
                      </div>
                      <Button
                        onClick={() => handleBarcodeSubmit(barcodeInput)}
                        className="bg-yellow-500 hover:bg-yellow-600 text-black"
                        data-testid="barcode-submit-btn"
                      >
                        <Search className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={startCameraScanner}
                        variant="outline"
                        className="border-yellow-500 text-yellow-700 hover:bg-yellow-100"
                        data-testid="camera-scan-btn"
                      >
                        <Camera className="w-4 h-4 mr-1" />
                        Camera
                      </Button>
                    </div>
                    <p className="text-xs text-yellow-700 mt-2">
                      Use USB scanner or click Camera to scan with device camera
                    </p>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {displayProducts.length === 0 ? (
                      <p className="col-span-2 text-center text-gray-500 py-8">No products found</p>
                    ) : (
                      displayProducts.map((product) => {
                        const qty = getProductQuantity(product.id);
                        const isScannedOutOfStock = scannedOutOfStockProduct?.id === product.id;
                        return (
                          <div
                            key={product.id}
                            className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${
                              isScannedOutOfStock 
                                ? 'border-red-500 border-2 bg-red-50 ring-2 ring-red-300' 
                                : 'border-gray-200'
                            }`}
                          >
                            {/* Out of Stock Badge for scanned product */}
                            {isScannedOutOfStock && (
                              <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded mb-2 text-center">
                                OUT OF STOCK - Cannot Add to Cart
                              </div>
                            )}
                            <div className="flex items-start justify-between mb-2">
                              <div>
                                <h3 className="font-semibold text-gray-800">{product.name}</h3>
                                <p className="text-xs text-gray-500">{product.brand}</p>
                              </div>
                              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                {product.category}
                              </span>
                            </div>
                            <div className="mt-2 space-y-1">
                              <p className="text-sm text-gray-600">
                                MRP: <span className="line-through text-gray-400">₹{product.price}</span>
                              </p>
                              <p className="text-sm font-semibold text-green-700">
                                Price: ₹{product.salesPrice}
                              </p>
                              <p className="text-xs text-gray-500">
                                Stock: <span className={product.stock <= 0 ? 'text-red-600 font-bold' : (product.stock < 10 ? 'text-orange-600 font-semibold' : 'font-semibold')}>{product.stock} {product.unit}</span>
                              </p>
                              {product.barcode && (
                                <p className="text-xs text-gray-400 font-mono">
                                  <Barcode className="w-3 h-3 inline mr-1" />
                                  {product.barcode}
                                </p>
                              )}
                              {product.category === 'Bakery' && product.expiry && (
                                <p className="text-xs text-blue-600">
                                  Expiry: {new Date(product.expiry).toLocaleDateString('en-IN')}
                                </p>
                              )}
                            </div>
                            
                            {/* Quantity Selector */}
                            <div className="mt-3 flex items-center space-x-2">
                              <Button
                                onClick={() => setProductQuantity(product.id, qty - 1, product.stock)}
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                disabled={qty <= 1}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input
                                type="number"
                                value={qty}
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1;
                                  setProductQuantity(product.id, val, product.stock);
                                }}
                                className="h-8 w-16 text-center"
                                min="1"
                                max={product.stock}
                              />
                              <Button
                                onClick={() => setProductQuantity(product.id, qty + 1, product.stock)}
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                disabled={qty >= product.stock}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            <Button 
                              onClick={() => addToCart(product, qty)}
                              className="w-full mt-3 bg-yellow-400 hover:bg-yellow-500 text-black"
                              disabled={product.stock === 0}
                            >
                              {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                            </Button>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Cart Section */}
          {(selectedAction === 'Billing' || !selectedAction) && (
            <div className="lg:col-span-1">
              <Card className="bg-white sticky top-6">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg">Cart</CardTitle>
                    {/* Held Carts Dropdown */}
                    {heldCarts.length > 0 && (
                      <Select value={selectedHeldCart?.toString() || ''} onValueChange={(val) => setSelectedHeldCart(val ? parseInt(val) : null)}>
                        <SelectTrigger className="w-[140px] h-8 text-xs">
                          <SelectValue placeholder={`Held (${heldCarts.length})`} />
                        </SelectTrigger>
                        <SelectContent>
                          {heldCarts.map((cart, idx) => (
                            <SelectItem key={cart.id} value={cart.id.toString()}>
                              Cart {idx + 1} - ₹{cart.total.toFixed(2)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  
                  {/* Held Cart Actions */}
                  {selectedHeldCart && (
                    <div className="flex gap-2 mt-2">
                      <Button
                        onClick={() => restoreHeldCart(selectedHeldCart)}
                        size="sm"
                        className="flex-1 h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        Restore
                      </Button>
                      <Button
                        onClick={() => clearHeldCart(selectedHeldCart)}
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-xs border-red-500 text-red-600 hover:bg-red-50"
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {cartItems.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">Cart is empty</p>
                    ) : (
                      <>
                        {/* Cart Items - Scrollable when more than 3 items */}
                        <div className={`space-y-3 ${cartItems.length > 3 ? 'max-h-[180px] overflow-y-auto pr-1' : ''}`}>
                          {cartItems.map((item) => (
                            <div key={item.id} className="border-b pb-3">
                              <div className="flex justify-between items-start mb-2">
                                <span className="font-medium text-gray-800">{item.name}</span>
                                <Button
                                  onClick={() => removeFromCart(item.id)}
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 hover:bg-red-100"
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <Button
                                    onClick={() => updateQuantity(item.id, -1)}
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                  >
                                    <Minus className="w-4 h-4" />
                                  </Button>
                                  <span className="w-8 text-center font-semibold">{item.quantity}</span>
                                  <Button
                                    onClick={() => updateQuantity(item.id, 1)}
                                    variant="outline"
                                    size="sm"
                                    className="h-8 w-8 p-0"
                                  >
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                                <span className="font-semibold">
                                  ₹{((selectedAction === 'Cost Price' ? item.costPrice : item.salesPrice) * item.quantity).toFixed(2)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-4 border-t-2 border-gray-300">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-sm text-gray-600">Subtotal:</span>
                            <span className="text-lg font-semibold text-gray-700">₹{calculateTotal().toFixed(2)}</span>
                          </div>
                          
                          {/* Discount Section */}
                          <div className="bg-gray-50 p-3 rounded-lg mb-3 space-y-2">
                            <p className="text-sm font-medium text-gray-700">Apply Discount</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-xs text-gray-500">Discount %</Label>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="0"
                                  value={discountPercent}
                                  onChange={(e) => handleDiscountPercentChange(e.target.value)}
                                  className="h-9 text-center"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500">Discount ₹</Label>
                                <Input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="0.00"
                                  value={discountAmount}
                                  onChange={(e) => handleDiscountAmountChange(e.target.value)}
                                  className="h-9 text-center"
                                />
                              </div>
                            </div>
                            {calculateDiscount() > 0 && (
                              <div className="flex justify-between items-center text-green-600 text-sm">
                                <span>Discount Applied:</span>
                                <span className="font-semibold">-₹{calculateDiscount().toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Final Total */}
                          <div className="flex justify-between items-center mb-4 bg-yellow-50 p-3 rounded-lg">
                            <span className="text-lg font-bold">Total:</span>
                            <span className="text-2xl font-bold text-gray-900">₹{calculateFinalTotal().toFixed(2)}</span>
                          </div>
                          
                          <div className="space-y-2">
                            <Button
                              onClick={handleBilling}
                              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold h-12"
                            >
                              <Receipt className="w-5 h-5 mr-2" />
                              Complete Billing
                            </Button>
                            
                            {/* Hold Cart Button */}
                            <Button
                              onClick={holdCurrentCart}
                              disabled={heldCarts.length >= 2}
                              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold h-12 disabled:opacity-50"
                            >
                              <PauseCircle className="w-5 h-5 mr-2" />
                              Hold Cart {heldCarts.length > 0 ? `(${heldCarts.length}/2)` : ''}
                            </Button>
                            
                            <Button
                              onClick={handleReturn}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold h-12"
                            >
                              <RotateCcw className="w-5 h-5 mr-2" />
                              Process Return
                            </Button>
                            <Button
                              onClick={handleDiscard}
                              variant="outline"
                              className="w-full border-red-500 text-red-600 hover:bg-red-50 font-semibold h-12"
                            >
                              <Trash2 className="w-5 h-5 mr-2" />
                              Discard Cart
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Payment Method Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => {
        setShowPaymentDialog(open);
        if (!open) setShowOnlineSubMenu(false);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Select Payment Method</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="text-center mb-6">
              {calculateDiscount() > 0 && (
                <div className="mb-2">
                  <p className="text-sm text-gray-500 line-through">₹{calculateTotal().toFixed(2)}</p>
                  <p className="text-xs text-green-600">Discount: -₹{calculateDiscount().toFixed(2)}</p>
                </div>
              )}
              <p className="text-sm text-gray-600 mb-2">Total Amount</p>
              <p className="text-3xl font-bold text-gray-900">₹{calculateFinalTotal().toFixed(2)}</p>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => handlePaymentMethod('UPI')}
                className="h-24 flex flex-col items-center justify-center space-y-2 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Smartphone className="w-8 h-8" />
                <span className="font-semibold">UPI</span>
              </Button>
              
              <Button
                onClick={() => handlePaymentMethod('Card')}
                className="h-24 flex flex-col items-center justify-center space-y-2 bg-blue-600 hover:bg-blue-700 text-white"
              >
                <CreditCard className="w-8 h-8" />
                <span className="font-semibold">Card</span>
              </Button>
              
              <Button
                onClick={() => handlePaymentMethod('Cash')}
                className="h-24 flex flex-col items-center justify-center space-y-2 bg-green-600 hover:bg-green-700 text-white"
              >
                <Banknote className="w-8 h-8" />
                <span className="font-semibold">Cash</span>
              </Button>
              
              <Button
                onClick={() => handlePaymentMethod('Split')}
                className="h-24 flex flex-col items-center justify-center space-y-2 bg-yellow-400 hover:bg-yellow-500 text-black"
              >
                <Split className="w-8 h-8" />
                <span className="font-semibold">Split</span>
              </Button>
              
              {/* Online Payment Button */}
              <Button
                onClick={() => handlePaymentMethod('Online')}
                className="h-24 flex flex-col items-center justify-center space-y-2 bg-orange-500 hover:bg-orange-600 text-white col-span-2"
              >
                <Globe className="w-8 h-8" />
                <span className="font-semibold">Online (Zomato/Swiggy)</span>
              </Button>
            </div>
            
            {/* Online Sub-Menu */}
            {showOnlineSubMenu && (
              <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm font-medium text-gray-700 mb-3 text-center">Select Platform</p>
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handlePaymentMethod('Online - Zomato')}
                    className="h-16 flex flex-col items-center justify-center space-y-1 bg-red-500 hover:bg-red-600 text-white"
                  >
                    <span className="font-bold text-lg">Zomato</span>
                  </Button>
                  <Button
                    onClick={() => handlePaymentMethod('Online - Swiggy')}
                    className="h-16 flex flex-col items-center justify-center space-y-1 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    <span className="font-bold text-lg">Swiggy</span>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Split Payment Dialog */}
      <Dialog open={showSplitDialog} onOpenChange={setShowSplitDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Split Payment</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-gray-100 p-4 rounded-lg text-center">
              <p className="text-sm text-gray-600 mb-1">Bill Total</p>
              <p className="text-2xl font-bold text-gray-900">₹{calculateFinalTotal().toFixed(2)}</p>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Card Amount</label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="0"
                    value={splitPayment.card}
                    onChange={(e) => setSplitPayment({...splitPayment, card: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Cash Amount</label>
                <div className="relative">
                  <Banknote className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="0"
                    value={splitPayment.cash}
                    onChange={(e) => setSplitPayment({...splitPayment, cash: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">UPI Amount</label>
                <div className="relative">
                  <Smartphone className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <Input
                    type="number"
                    placeholder="0"
                    value={splitPayment.upi}
                    onChange={(e) => setSplitPayment({...splitPayment, upi: e.target.value})}
                    className="pl-10"
                  />
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-400 p-4 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Split Total:</span>
                <span className="text-xl font-bold text-gray-900">₹{calculateSplitTotal().toFixed(2)}</span>
              </div>
              {Math.abs(calculateSplitTotal() - calculateFinalTotal()) > 0.01 && calculateSplitTotal() > 0 && (
                <p className="text-xs text-red-600 mt-2">
                  Split total doesn't match bill total!
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleFinalizeSplit}
              disabled={Math.abs(calculateSplitTotal() - calculateFinalTotal()) > 0.01 || calculateSplitTotal() === 0}
              className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-semibold h-12 disabled:opacity-50"
            >
              Finalize Bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill Dialog */}
      <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
        <DialogContent className="sm:max-w-md p-0 bg-white">
          <div className="w-full bg-white overflow-auto" style={{ maxHeight: '500px' }}>
            {finalBillData && (
              <div className="p-3 text-black bg-white" style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '11px', lineHeight: '1.3' }}>
                {/* Header */}
                <div className="text-center text-xs mb-1">See you again</div>
                <div className="text-center font-bold text-sm mb-2">RETAIL INVOICE</div>
                
                {/* Shop Name */}
                <div className="text-center font-bold text-base my-2 py-1 border-t border-b border-black">New Shop</div>
                
                {/* Invoice Info */}
                <div className="space-y-0.5 text-xs">
                  <div className="flex justify-between"><span>Invoice No:</span><span>{finalBillData.invoice_no}</span></div>
                  <div className="flex justify-between"><span>Store</span><span>Thane Mira Bhayandar</span></div>
                  <div className="flex justify-between"><span>Address</span><span className="text-right text-[9px] max-w-[55%]">Ground Floor Shop No 5 & 6 Ostwal Ashadeep Near Silver Park Signal East Mira Bhayandar Road Thane Maharashtra 401107</span></div>
                  <div className="flex justify-between"><span>Pincode</span><span>401107</span></div>
                  <div className="flex justify-between"><span>Phone</span><span>7718936910</span></div>
                  <div className="flex justify-between"><span>GSTIN</span><span>27BSTPM1629P1ZU</span></div>
                  <div className="flex justify-between"><span>FSSAI</span><span>11525020000225</span></div>
                  <div className="flex justify-between"><span>Date</span><span>{finalBillData.date}</span></div>
                  <div className="flex justify-between"><span>Time</span><span>{finalBillData.time}</span></div>
                </div>
                
                <hr className="border-dashed border-black my-2" />
                
                {/* Payment Info */}
                <div className="flex justify-between text-xs font-bold">
                  <span>Payment Type</span>
                  <span>Order From</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span>{finalBillData.payment_method === 'Split' && finalBillData.payment_details 
                    ? 'Split - ' + [
                        finalBillData.payment_details.cash ? `Cash ₹${parseFloat(finalBillData.payment_details.cash).toFixed(2)}` : '',
                        finalBillData.payment_details.upi ? `UPI ₹${parseFloat(finalBillData.payment_details.upi).toFixed(2)}` : '',
                        finalBillData.payment_details.card ? `Card ₹${parseFloat(finalBillData.payment_details.card).toFixed(2)}` : ''
                      ].filter(Boolean).join(' + ')
                    : (finalBillData.payment_method || 'Cash')}</span>
                  <span>pos</span>
                </div>
                
                <hr className="border-dashed border-black my-2" />
                
                {/* Product Table */}
                <table className="w-full text-[9px]">
                  <thead>
                    <tr className="border-b border-dashed border-black">
                      <th className="text-left py-0.5">Product</th>
                      <th className="text-center py-0.5">Qty</th>
                      <th className="text-center py-0.5">MRP</th>
                      <th className="text-center py-0.5">Selling Price</th>
                      <th className="text-right py-0.5">Amt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finalBillData.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-left py-0.5 break-words max-w-[80px]">{item.name}</td>
                        <td className="text-center py-0.5">{item.quantity}</td>
                        <td className="text-center py-0.5">{(item.price || 0).toFixed(2)}</td>
                        <td className="text-center py-0.5">{(item.sales_price || item.price || 0).toFixed(2)}</td>
                        <td className="text-right py-0.5">{(item.item_total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Quantity & Total */}
                <div className="flex justify-between text-xs font-bold border-t border-dashed border-black mt-2 pt-1">
                  <span>Quantity</span>
                  <span>{finalBillData.total_quantity}</span>
                  <span>Total</span>
                  <span>{(finalBillData.subtotal + finalBillData.cgst + finalBillData.sgst).toFixed(2)}</span>
                </div>
                
                <hr className="border-dashed border-black my-2" />
                
                {/* GST Breakdown */}
                <table className="w-full text-[9px]">
                  <thead>
                    <tr className="border-b border-dashed border-black">
                      <td className="text-center font-bold">CGST</td>
                      <td className="text-center font-bold">SGST</td>
                      <td className="text-center font-bold">CESS</td>
                      <td className="text-center font-bold">ACESS</td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-center">{finalBillData.cgst.toFixed(2)}</td>
                      <td className="text-center">{finalBillData.sgst.toFixed(2)}</td>
                      <td className="text-center">0.00</td>
                      <td className="text-center">0.00</td>
                    </tr>
                  </tbody>
                </table>
                
                <hr className="border-dashed border-black my-2" />
                
                {/* Final Totals */}
                <div className="space-y-0.5 text-xs">
                  <div className="flex justify-between"><span>Total Items</span><span>{finalBillData.items.length}</span></div>
                  <div className="flex justify-between"><span>Order Amount</span><span>{(finalBillData.subtotal + finalBillData.cgst + finalBillData.sgst).toFixed(2)}</span></div>
                </div>
                
                <div className="flex justify-between text-sm font-bold border-y-2 border-black py-1 my-2">
                  <span>TOTAL PAYABLE</span>
                  <span>{finalBillData.total.toFixed(2)}</span>
                </div>
                
                {/* Savings */}
                <div className="flex justify-between text-base font-bold my-3">
                  <span>TOTAL SAVINGS</span>
                  <span>{finalBillData.savings.toFixed(2)}</span>
                </div>
                
                {/* Footer */}
                <div className="text-center text-[9px] font-bold border-t-2 border-black pt-2">
                  <div>THANK YOU FOR SHOPPING WITH US</div>
                  <div>NO RETURN - NO EXCHANGE AFTER BILLING</div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="p-4 border-t">
            <div className="flex space-x-2 w-full">
              <Button onClick={printBill} className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-black font-semibold">
                Print Bill
              </Button>
              <Button onClick={() => setShowBillDialog(false)} variant="outline" className="flex-1">
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Management Dialog */}
      <Dialog open={showProductDialog} onOpenChange={(open) => {
        setShowProductDialog(open);
        if (!open) {
          navigate('/dashboard');
        }
      }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Product Management</DialogTitle>
          </DialogHeader>
          
          <Tabs value={productDialogTab} onValueChange={setProductDialogTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="add">Add Product</TabsTrigger>
              <TabsTrigger value="update">Update Products</TabsTrigger>
              <TabsTrigger value="categories">Manage Categories</TabsTrigger>
            </TabsList>
            
            {/* Add New Product Tab */}
            {productDialogTab === 'add' && (
              <div className="py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Product Name *</Label>
                    <Input
                      value={newProduct.name}
                      onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                      placeholder="Enter product name"
                    />
                  </div>
                  <div>
                    <Label>Brand *</Label>
                    <Input
                      value={newProduct.brand}
                      onChange={(e) => setNewProduct({...newProduct, brand: e.target.value})}
                      placeholder="Enter brand name"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>MRP (₹)</Label>
                    <Input
                      type="number"
                      value={newProduct.mrp}
                      onChange={(e) => setNewProduct({...newProduct, mrp: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Cost Price (₹) *</Label>
                    <Input
                      type="number"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Sales Price (₹) *</Label>
                    <Input
                      type="number"
                      value={newProduct.sales_price}
                      onChange={(e) => setNewProduct({...newProduct, sales_price: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Category *</Label>
                    <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {dbCategories.filter(c => c.name !== 'All').map(cat => (
                          <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select value={newProduct.unit} onValueChange={(v) => setNewProduct({...newProduct, unit: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pcs">Pcs</SelectItem>
                        <SelectItem value="Kg">Kg</SelectItem>
                        <SelectItem value="Malt">Malt</SelectItem>
                        <SelectItem value="Ltr">Ltr</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Stock Quantity *</Label>
                    <Input
                      type="number"
                      value={newProduct.stock}
                      onChange={(e) => setNewProduct({...newProduct, stock: e.target.value})}
                      placeholder="0"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>GST %</Label>
                    <Select value={newProduct.gst} onValueChange={(v) => setNewProduct({...newProduct, gst: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">0%</SelectItem>
                        <SelectItem value="5">5%</SelectItem>
                        <SelectItem value="12">12%</SelectItem>
                        <SelectItem value="18">18%</SelectItem>
                        <SelectItem value="28">28%</SelectItem>
                        <SelectItem value="40">40%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>HSN Code *</Label>
                    <Input
                      value={newProduct.hsn_code}
                      onChange={(e) => setNewProduct({...newProduct, hsn_code: e.target.value})}
                      placeholder="Enter HSN code"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Barcode (EAN/UPC)</Label>
                  <div className="relative">
                    <Barcode className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                    <Input
                      value={newProduct.barcode}
                      onChange={(e) => setNewProduct({...newProduct, barcode: e.target.value})}
                      placeholder="Scan or enter barcode"
                      className="pl-10 font-mono"
                    />
                  </div>
                </div>
                
                {newProduct.category === 'Bakery' && (
                  <div>
                    <Label>Expiry Date</Label>
                    <Input
                      type="date"
                      value={newProduct.expiry}
                      onChange={(e) => setNewProduct({...newProduct, expiry: e.target.value})}
                    />
                  </div>
                )}
                
                <Button onClick={handleAddNewProduct} className="w-full bg-green-600 hover:bg-green-700">
                  <Package className="w-4 h-4 mr-2" />
                  Add Product
                </Button>
              </div>
            )}
            
            {/* Update Products Tab */}
            {productDialogTab === 'update' && (
              <div className="py-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Filter by Category</Label>
                    <Select value={updateCategoryFilter} onValueChange={setUpdateCategoryFilter}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">All Categories</SelectItem>
                        {dbCategories.filter(c => c.name !== 'All').map(cat => (
                          <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Search Product</Label>
                    <Input
                      value={updateProductSearch}
                      onChange={(e) => setUpdateProductSearch(e.target.value)}
                      placeholder="Search by name..."
                    />
                  </div>
                </div>
                
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {products
                    .filter(p => (updateCategoryFilter === 'All' || p.category === updateCategoryFilter) &&
                      p.name.toLowerCase().includes(updateProductSearch.toLowerCase()))
                    .map(product => (
                      <div 
                        key={product.id} 
                        className={`p-3 border rounded cursor-pointer hover:bg-gray-50 ${
                          selectedProductForUpdate?.id === product.id ? 'border-yellow-400 bg-yellow-50' : ''
                        }`}
                        onClick={() => {
                          setSelectedProductForUpdate(product);
                          setUpdateData({
                            name: product.name,
                            mrp: product.mrp || product.price,
                            sales_price: product.salesPrice,
                            add_stock: '',
                            expiry: product.expiry || ''
                          });
                        }}
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium">{product.name}</p>
                            <p className="text-sm text-gray-500">{product.category} • Stock: {product.stock}</p>
                          </div>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProduct(product.id);
                            }}
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                </div>
                
                {selectedProductForUpdate && (
                  <div className="border-t pt-4 space-y-4">
                    <h4 className="font-semibold">Update: {selectedProductForUpdate.name}</h4>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Product Name</Label>
                        <Input
                          value={updateData.name}
                          onChange={(e) => setUpdateData({...updateData, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Add Stock</Label>
                        <Input
                          type="number"
                          value={updateData.add_stock}
                          onChange={(e) => setUpdateData({...updateData, add_stock: e.target.value})}
                          placeholder="Enter quantity to add"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>MRP (₹)</Label>
                        <Input
                          type="number"
                          value={updateData.mrp}
                          onChange={(e) => setUpdateData({...updateData, mrp: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Sales Price (₹)</Label>
                        <Input
                          type="number"
                          value={updateData.sales_price}
                          onChange={(e) => setUpdateData({...updateData, sales_price: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Category</Label>
                        <Select value={updateData.category || selectedProductForUpdate.category} onValueChange={(v) => setUpdateData({...updateData, category: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {dbCategories.filter(c => c.name !== 'All').map(cat => (
                              <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Expiry Date</Label>
                        <Input
                          type="date"
                          value={updateData.expiry}
                          onChange={(e) => setUpdateData({...updateData, expiry: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <Button onClick={handleUpdateProduct} className="w-full bg-blue-600 hover:bg-blue-700">
                      Update Product
                    </Button>
                  </div>
                )}
              </div>
            )}
            
            {/* Manage Categories Tab */}
            {productDialogTab === 'categories' && (
              <div className="py-4 space-y-4">
                {/* Add New Category */}
                <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                  <h3 className="font-semibold text-gray-700">Add New Category</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Category Name *</Label>
                      <Input
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                        placeholder="Enter category name"
                      />
                    </div>
                    <div>
                      <Label>Icon</Label>
                      <Select value={newCategoryIcon} onValueChange={setNewCategoryIcon}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ShoppingBag">Shopping Bag</SelectItem>
                          <SelectItem value="Coffee">Coffee</SelectItem>
                          <SelectItem value="Cake">Cake</SelectItem>
                          <SelectItem value="Wine">Wine/Beverage</SelectItem>
                          <SelectItem value="Package">Package</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleAddCategory} className="w-full bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category
                  </Button>
                </div>
                
                {/* Existing Categories */}
                <div className="space-y-2">
                  <h3 className="font-semibold text-gray-700">Existing Categories</h3>
                  <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {dbCategories.filter(c => c.name !== 'All').map((category) => {
                      const IconComponent = iconMap[category.icon] || Package;
                      return (
                        <div key={category.id} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                          {editingCategory?.id === category.id ? (
                            <div className="flex-1 grid grid-cols-2 gap-2 mr-2">
                              <Input
                                value={editingCategory.name}
                                onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                                placeholder="Category name"
                              />
                              <Select value={editingCategory.icon} onValueChange={(v) => setEditingCategory({...editingCategory, icon: v})}>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="ShoppingBag">Shopping Bag</SelectItem>
                                  <SelectItem value="Coffee">Coffee</SelectItem>
                                  <SelectItem value="Cake">Cake</SelectItem>
                                  <SelectItem value="Wine">Wine/Beverage</SelectItem>
                                  <SelectItem value="Package">Package</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              <IconComponent className="w-5 h-5 text-gray-600" />
                              <span className="font-medium">{category.name}</span>
                            </div>
                          )}
                          <div className="flex gap-2">
                            {editingCategory?.id === category.id ? (
                              <>
                                <Button size="sm" onClick={handleUpdateCategory} className="bg-green-600 hover:bg-green-700 h-8">
                                  Save
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setEditingCategory(null)} className="h-8">
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => setEditingCategory({...category})}
                                  className="h-8"
                                >
                                  Edit
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  onClick={() => handleDeleteCategory(category.id, category.name)}
                                  className="h-8 border-red-500 text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <p className="text-xs text-gray-500 text-center">
                  Note: Categories with assigned products cannot be deleted. Reassign products first.
                </p>
              </div>
            )}
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Discard Reason Dialog */}
      <Dialog open={showDiscardReasonDialog} onOpenChange={setShowDiscardReasonDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Discard Reason</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Reason</Label>
            <Select value={discardReason} onValueChange={setDiscardReason}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="spoiled">Spoiled</SelectItem>
                <SelectItem value="quality">Quality Issue</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button onClick={handleFinalDiscard} className="w-full bg-red-600 hover:bg-red-700">
              Confirm Discard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Camera Scanner Dialog */}
      <Dialog open={showCameraScanner} onOpenChange={(open) => !open && stopCameraScanner()}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center space-x-2">
              <Camera className="w-5 h-5" />
              <span>Camera Barcode Scanner</span>
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div id="barcode-scanner" className="w-full"></div>
            <p className="text-sm text-gray-500 mt-4 text-center">
              Point your camera at a barcode to scan
            </p>
          </div>
          <DialogFooter>
            <Button onClick={stopCameraScanner} variant="outline" className="w-full">
              Close Scanner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Sales;
